import { UniversalSchemaType } from "universal-schema/lib/0.0.4"
import { isJSONSchema7, SchemaForm } from "universal-schema/lib"

export interface ChoiceData {
    tag: string
    value: string
}

export interface MultipleChoiceData {
    choices: ChoiceData[]
    minSelections?: number
    maxSelections?: number
    selections?: ChoiceData[]
}

export function parseSchemaType(
    rawType: string
): UniversalSchemaType | undefined {
    if (
        !Object.values(UniversalSchemaType).includes(
            rawType as UniversalSchemaType
        )
    ) {
        return undefined
    }

    return rawType as UniversalSchemaType
}

export function mustParseSchemaType(rawType: string): UniversalSchemaType {
    const schemaType = parseSchemaType(rawType)

    if (schemaType === undefined) {
        throw new Error("unknown type")
    }

    return schemaType
}

export function traverseSchema<T>(
    rootSchema: SchemaForm,
    rootValue: any,
    visit: (
        passed: T,
        schema: SchemaForm,
        value: any,
        path: (string | number)[]
    ) => T,
    initialValue: T
): T {
    function traverse(
        passed: T,
        { schema, uiSchema }: SchemaForm,
        value: any,
        path: (string | number)[]
    ): T {
        if (schema.$ref !== undefined)
            return visit(passed, { schema, uiSchema }, value, path)

        if (schema.allOf !== undefined) {
            return schema.allOf.reduce(
                (innerPassed, one) =>
                    isJSONSchema7(one)
                        ? traverse(
                              innerPassed,
                              {
                                  schema: {
                                      ...schema,
                                      allOf: undefined,
                                      ...one
                                  },
                                  uiSchema
                              },
                              value,
                              path
                          )
                        : innerPassed,
                passed
            )
        }

        if (schema.anyOf !== undefined) {
            return schema.anyOf.reduce(
                (innerPassed, one) =>
                    isJSONSchema7(one)
                        ? traverse(
                              innerPassed,
                              {
                                  schema: {
                                      ...schema,
                                      anyOf: undefined,
                                      ...one
                                  },
                                  uiSchema
                              },
                              value,
                              path
                          )
                        : innerPassed,
                passed
            )
        }

        if (schema.if !== undefined) {
            const thenTraversed =
                schema.then === undefined || !isJSONSchema7(schema.then)
                    ? passed
                    : traverse(
                          passed,
                          {
                              schema: {
                                  ...schema,
                                  if: undefined,
                                  then: undefined,
                                  else: undefined,
                                  ...schema.then,
                                  properties: {
                                      ...schema.properties,
                                      ...schema.then.properties
                                  }
                              },
                              uiSchema
                          },
                          value,
                          path
                      )

            return !isJSONSchema7(schema.else)
                ? thenTraversed
                : traverse(
                      thenTraversed,
                      {
                          schema: {
                              ...schema,
                              if: undefined,
                              then: undefined,
                              else: undefined,
                              ...schema.else,
                              properties: {
                                  ...schema.properties,
                                  ...schema.else.properties
                              }
                          },
                          uiSchema
                      },
                      value,
                      path
                  )
        }

        if (schema.type === "object" && schema.properties !== undefined) {
            return Object.entries(schema.properties).reduce(
                (innerPassed, [key, innerSchema]) => {
                    return !isJSONSchema7(innerSchema)
                        ? innerPassed
                        : traverse(
                              innerPassed,
                              {
                                  schema: innerSchema,
                                  uiSchema: uiSchema[key] ?? {}
                              },
                              typeof value === "object" && value !== null
                                  ? value[key]
                                  : undefined,
                              path.concat([key])
                          )
                },
                visit(passed, { schema, uiSchema }, value, path)
            )
        }

        if (schema.type === "array") {
            if (Array.isArray(schema.items)) {
                return schema.items.reduce((p, s, i) => {
                    return isJSONSchema7(s)
                        ? traverse(
                              p,
                              { schema: s, uiSchema: uiSchema["items"] ?? {} },
                              Array.isArray(value) ? value[i] : undefined,
                              path.concat([i])
                          )
                        : p
                }, visit(passed, { schema, uiSchema }, value, path))
            }

            if (isJSONSchema7(schema.items)) {
                const arrayVisited = visit(
                    passed,
                    { schema, uiSchema },
                    value,
                    path
                )

                return traverse(
                    arrayVisited,
                    { schema: schema.items, uiSchema: uiSchema["items"] ?? {} },
                    Array.isArray(value) ? value[0] : undefined,
                    path.concat([0])
                )
            }
        }

        return visit(passed, { schema, uiSchema }, value, path)
    }

    return traverse(initialValue, rootSchema, rootValue, [])
}
